import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Lock, 
  LogOut, 
  Users, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Home,
  TrendingUp,
  BarChart3,
  Target,
  ArrowRight,
  Mail,
  FileText,
  Send,
  Zap,
  Gift,
  Building2,
  MousePointerClick,
  Tag
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

// Import email marketing sections
import LeadsSection from "@/components/admin/LeadsSection";
import TemplatesSection from "@/components/admin/TemplatesSection";
import CampaignsSection from "@/components/admin/CampaignsSection";
import AutomationsSection from "@/components/admin/AutomationsSection";
import PartnersSection from "@/components/admin/PartnersSection";
import BVWaitlistSection from "@/components/admin/BVWaitlistSection";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const outcomeLabels = {
  laag_risico: { label: "Laag risico", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
  twijfelgeval: { label: "Twijfelgeval", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
  verhoogd_risico: { label: "Verhoogd risico", icon: XCircle, color: "text-red-600", bg: "bg-red-50" }
};

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [activeTab, setActiveTab] = useState("analytics");
  const [discountLeads, setDiscountLeads] = useState({ leads: [], total: 0, converted: 0, conversion_rate: 0 });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/admin/login`, { password });
      setIsAuthenticated(true);
      localStorage.setItem("admin_password", password);
      await fetchData(password);
    } catch (error) {
      toast.error("Ongeldig wachtwoord");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async (pwd) => {
    setIsLoading(true);
    try {
      const [submissionsRes, statsRes, analyticsRes, discountLeadsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/submissions?password=${pwd}`),
        axios.get(`${API_URL}/admin/stats?password=${pwd}`),
        axios.get(`${API_URL}/admin/analytics?password=${pwd}`),
        axios.get(`${API_URL}/admin/referral-leads?password=${pwd}`).catch(() => ({ data: { leads: [], total: 0, converted: 0, conversion_rate: 0 } }))
      ]);
      setSubmissions(submissionsRes.data);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setDiscountLeads(discountLeadsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kon gegevens niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    setSubmissions([]);
    setStats(null);
    setAnalytics(null);
    localStorage.removeItem("admin_password");
  };

  // Check for saved password on mount
  useEffect(() => {
    const savedPassword = localStorage.getItem("admin_password");
    if (savedPassword) {
      setPassword(savedPassword);
      setIsAuthenticated(true);
      fetchData(savedPassword);
    }
  }, []);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-outfit text-2xl font-medium text-zinc-900 mb-2" data-testid="admin-title">
                Admin Login
              </h1>
              <p className="text-zinc-500 text-sm">
                Voer het beheerderswachtwoord in
              </p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="mb-6">
                <Label htmlFor="password" className="text-zinc-700 font-medium">
                  Wachtwoord
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Voer wachtwoord in"
                  className="mt-2 rounded-xl border-zinc-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 h-12"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading || !password}
                className="w-full rounded-full py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white"
                data-testid="admin-login-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Inloggen"
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/" 
                className="text-sm text-zinc-500 hover:text-blue-600 transition-colors"
                data-testid="back-home-link"
              >
                Terug naar homepage
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/" 
              className="text-zinc-600 hover:text-zinc-900 transition-colors"
              data-testid="admin-home-link"
            >
              <Home className="w-5 h-5" />
            </Link>
            <h1 className="font-outfit font-medium text-zinc-900">Proofix Admin</h1>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-full px-4 py-2 border-zinc-300 text-zinc-600 hover:bg-zinc-100"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Uitloggen
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="px-6 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Stats Cards */}
          {stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
            >
              <div className="bg-white border border-zinc-200/60 rounded-xl p-5" data-testid="stat-total">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.total}</p>
                    <p className="text-xs text-zinc-500">Totaal</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-zinc-200/60 rounded-xl p-5" data-testid="stat-laag">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.laag_risico}</p>
                    <p className="text-xs text-zinc-500">Laag risico</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-zinc-200/60 rounded-xl p-5" data-testid="stat-twijfel">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.twijfelgeval}</p>
                    <p className="text-xs text-zinc-500">Twijfelgeval</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-zinc-200/60 rounded-xl p-5" data-testid="stat-verhoogd">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-outfit font-medium text-zinc-900">{stats.verhoogd_risico}</p>
                    <p className="text-xs text-zinc-500">Verhoogd</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Button
              variant={activeTab === "analytics" ? "default" : "outline"}
              onClick={() => setActiveTab("analytics")}
              className={`rounded-full px-4 py-2 ${activeTab === "analytics" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-analytics"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button
              variant={activeTab === "submissions" ? "default" : "outline"}
              onClick={() => setActiveTab("submissions")}
              className={`rounded-full px-4 py-2 ${activeTab === "submissions" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-submissions"
            >
              <Users className="w-4 h-4 mr-2" />
              Inzendingen
            </Button>
            
            {/* Divider */}
            <div className="w-px h-8 bg-zinc-200 mx-2 self-center" />
            
            {/* Email Marketing Tabs */}
            <Button
              variant={activeTab === "leads" ? "default" : "outline"}
              onClick={() => setActiveTab("leads")}
              className={`rounded-full px-4 py-2 ${activeTab === "leads" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-leads"
            >
              <Mail className="w-4 h-4 mr-2" />
              Leads
            </Button>
            <Button
              variant={activeTab === "templates" ? "default" : "outline"}
              onClick={() => setActiveTab("templates")}
              className={`rounded-full px-4 py-2 ${activeTab === "templates" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-templates"
            >
              <FileText className="w-4 h-4 mr-2" />
              Templates
            </Button>
            <Button
              variant={activeTab === "campaigns" ? "default" : "outline"}
              onClick={() => setActiveTab("campaigns")}
              className={`rounded-full px-4 py-2 ${activeTab === "campaigns" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-campaigns"
            >
              <Send className="w-4 h-4 mr-2" />
              Campaigns
            </Button>
            <Button
              variant={activeTab === "automations" ? "default" : "outline"}
              onClick={() => setActiveTab("automations")}
              className={`rounded-full px-4 py-2 ${activeTab === "automations" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-automations"
            >
              <Zap className="w-4 h-4 mr-2" />
              Automations
            </Button>
            
            {/* Divider */}
            <div className="w-px h-8 bg-zinc-200 mx-2 self-center" />
            
            {/* Partners Tab */}
            <Button
              variant={activeTab === "partners" ? "default" : "outline"}
              onClick={() => setActiveTab("partners")}
              className={`rounded-full px-4 py-2 ${activeTab === "partners" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-partners"
            >
              <Gift className="w-4 h-4 mr-2" />
              Partners
            </Button>
            
            {/* Discount Leads Tab */}
            <Button
              variant={activeTab === "discount-leads" ? "default" : "outline"}
              onClick={() => setActiveTab("discount-leads")}
              className={`rounded-full px-4 py-2 ${activeTab === "discount-leads" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-discount-leads"
            >
              <Tag className="w-4 h-4 mr-2" />
              Korting Leads
            </Button>
            
            {/* BV Waitlist Tab */}
            <Button
              variant={activeTab === "bv-waitlist" ? "default" : "outline"}
              onClick={() => setActiveTab("bv-waitlist")}
              className={`rounded-full px-4 py-2 ${activeTab === "bv-waitlist" ? "bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white" : "border-zinc-300 text-zinc-600"}`}
              data-testid="tab-bv-waitlist"
            >
              <Building2 className="w-4 h-4 mr-2" />
              BV Waitlist
            </Button>
          </div>

          {/* Analytics Tab */}
          {activeTab === "analytics" && analytics && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Funnel Overview */}
              <div className="bg-white border border-zinc-200/60 rounded-2xl p-6">
                <h2 className="font-outfit text-lg font-medium text-zinc-900 mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  Conversie Funnel
                </h2>
                
                <div className="space-y-4">
                  {[
                    { key: "landing_page_view", label: "Landing Page Views", icon: "👁️" },
                    { key: "dba_check_started", label: "Check Gestart", icon: "▶️" },
                    { key: "dba_check_completed", label: "Check Voltooid", icon: "✅" },
                    { key: "premium_cta_clicked", label: "Premium CTA Clicks", icon: "🎯" },
                    { key: "payment_success", label: "Betalingen", icon: "💰" },
                    { key: "premium_report_viewed", label: "Rapport Bekeken", icon: "📊" }
                  ].map((step, index) => {
                    const count = analytics.funnel?.[step.key] || 0;
                    const maxCount = analytics.funnel?.landing_page_view || 1;
                    const percentage = Math.round((count / maxCount) * 100);
                    
                    return (
                      <div key={step.key} className="relative">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-zinc-600 flex items-center gap-2">
                            <span>{step.icon}</span>
                            {step.label}
                          </span>
                          <span className="text-sm font-medium text-zinc-900">{count}</span>
                        </div>
                        <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        {index < 5 && (
                          <div className="flex justify-center my-1">
                            <ArrowRight className="w-4 h-4 text-zinc-300 rotate-90" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Conversion Rates */}
              <div className="bg-white border border-zinc-200/60 rounded-2xl p-6">
                <h2 className="font-outfit text-lg font-medium text-zinc-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  Conversieratio's
                </h2>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { key: "landing_to_start", label: "Landing → Start" },
                    { key: "start_to_complete", label: "Start → Voltooid" },
                    { key: "complete_to_cta", label: "Voltooid → CTA" },
                    { key: "cta_to_payment", label: "CTA → Betaling" },
                    { key: "overall_conversion", label: "Totaal" }
                  ].map((rate) => {
                    const value = analytics.funnel?.conversion_rates?.[rate.key] || 0;
                    return (
                      <div key={rate.key} className="text-center p-4 bg-zinc-50 rounded-xl">
                        <p className="text-2xl font-outfit font-medium text-zinc-900">{value}%</p>
                        <p className="text-xs text-zinc-500 mt-1">{rate.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Events */}
              <div className="bg-white border border-zinc-200/60 rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-zinc-200">
                  <h2 className="font-outfit text-lg font-medium text-zinc-900">
                    Recente Events
                  </h2>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-50">
                        <TableHead className="font-medium">Tijdstip</TableHead>
                        <TableHead className="font-medium">Event</TableHead>
                        <TableHead className="font-medium">Submission ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.recent_events?.slice(0, 20).map((event, index) => (
                        <TableRow key={event.id || index}>
                          <TableCell className="text-sm text-zinc-600">
                            {event.timestamp ? new Date(event.timestamp).toLocaleString('nl-NL') : '-'}
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                              {event.event_type}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-zinc-500 font-mono">
                            {event.submission_id ? event.submission_id.slice(0, 8) + '...' : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </motion.div>
          )}

          {/* Email Marketing Sections */}
          {activeTab === "leads" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <LeadsSection password={password} />
            </motion.div>
          )}

          {activeTab === "templates" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <TemplatesSection password={password} />
            </motion.div>
          )}

          {activeTab === "campaigns" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CampaignsSection password={password} />
            </motion.div>
          )}

          {activeTab === "automations" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AutomationsSection password={password} />
            </motion.div>
          )}

          {/* Submissions Table */}
          {activeTab === "submissions" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-zinc-200/60 rounded-2xl overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-zinc-200">
              <h2 className="font-outfit text-lg font-medium text-zinc-900" data-testid="submissions-title">
                Inzendingen
              </h2>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-zinc-500">Nog geen inzendingen</p>
              </div>
            ) : (
              <Table data-testid="submissions-table">
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="font-medium">Datum</TableHead>
                    <TableHead className="font-medium">Naam</TableHead>
                    <TableHead className="font-medium">E-mail</TableHead>
                    <TableHead className="font-medium">Score</TableHead>
                    <TableHead className="font-medium">Uitkomst</TableHead>
                    <TableHead className="font-medium">Premium</TableHead>
                    <TableHead className="font-medium w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((submission) => {
                    const outcome = outcomeLabels[submission.outcome];
                    const OutcomeIcon = outcome.icon;
                    const isExpanded = expandedRow === submission.id;

                    return (
                      <React.Fragment key={submission.id}>
                        <TableRow 
                          className="cursor-pointer hover:bg-zinc-50"
                          onClick={() => setExpandedRow(isExpanded ? null : submission.id)}
                          data-testid={`submission-row-${submission.id}`}
                        >
                          <TableCell className="text-sm text-zinc-600">
                            {formatDate(submission.timestamp)}
                          </TableCell>
                          <TableCell className="font-medium text-zinc-900">
                            {submission.name || <span className="text-zinc-400 italic">Niet opgeslagen</span>}
                          </TableCell>
                          <TableCell className="text-sm text-zinc-600">
                            {submission.email || <span className="text-zinc-400">-</span>}
                          </TableCell>
                          <TableCell className="text-sm">
                            <span className="font-medium text-zinc-900">
                              {submission.normalized_score}
                            </span>
                            <span className="text-zinc-400">
                              /100
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${outcome.bg} ${outcome.color}`}>
                              <OutcomeIcon className="w-3.5 h-3.5" />
                              {outcome.label}
                            </span>
                          </TableCell>
                          <TableCell>
                            {submission.premium_unlocked ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                <CheckCircle2 className="w-3 h-3" />
                                Betaald
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (confirm(`Premium ontgrendelen voor ${submission.email || submission.id}?`)) {
                                    try {
                                      await axios.post(`${API_URL}/admin/unlock-premium?password=${password}&submission_id=${submission.id}`);
                                      toast.success("Premium ontgrendeld!");
                                      fetchData(password);
                                    } catch (err) {
                                      toast.error("Kon premium niet ontgrendelen");
                                    }
                                  }
                                }}
                              >
                                Ontgrendel
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-zinc-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-zinc-400" />
                            )}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-zinc-50 p-6">
                              <div>
                                <h4 className="font-medium text-zinc-900 mb-3">Antwoorden</h4>
                                <div className="grid gap-2">
                                  {submission.answers.map((answer, idx) => (
                                    <div 
                                      key={idx}
                                      className="flex items-center justify-between text-sm bg-white rounded-lg px-4 py-2 border border-zinc-200"
                                    >
                                      <span className="text-zinc-600">{answer.question_id}</span>
                                      <span className="text-zinc-900 font-medium">{answer.option_id}</span>
                                      <span className={`px-2 py-0.5 rounded text-xs ${
                                        answer.score === 0 ? "bg-emerald-100 text-emerald-700" :
                                        answer.score <= 1 ? "bg-amber-100 text-amber-700" :
                                        "bg-red-100 text-red-700"
                                      }`}>
                                        +{answer.score}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </motion.div>
          )}

          {/* Partners Tab */}
          {activeTab === "partners" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <PartnersSection password={password} />
            </motion.div>
          )}

          {/* Discount Leads Tab */}
          {activeTab === "discount-leads" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-zinc-200/60 rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit text-lg font-medium text-zinc-900 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-purple-600" />
                  Partner Korting Leads
                </h2>
                <div className="flex gap-4 text-sm">
                  <span className="text-zinc-500">
                    Totaal: <span className="font-semibold text-zinc-900">{discountLeads.total}</span>
                  </span>
                  <span className="text-zinc-500">
                    Geconverteerd: <span className="font-semibold text-emerald-600">{discountLeads.converted}</span>
                  </span>
                  <span className="text-zinc-500">
                    Conversie: <span className="font-semibold text-blue-600">{discountLeads.conversion_rate}%</span>
                  </span>
                </div>
              </div>

              {discountLeads.leads?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="font-medium">Datum</TableHead>
                      <TableHead className="font-medium">E-mail</TableHead>
                      <TableHead className="font-medium">Partner</TableHead>
                      <TableHead className="font-medium">Korting</TableHead>
                      <TableHead className="font-medium">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discountLeads.leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="text-sm text-zinc-600">
                          {new Date(lead.created_at).toLocaleDateString('nl-NL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell className="font-medium">{lead.email}</TableCell>
                        <TableCell className="text-sm">
                          <span className="text-purple-600">{lead.partner_name || lead.referral_code}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded text-sm">
                            {lead.discount_code}
                          </span>
                        </TableCell>
                        <TableCell>
                          {lead.payment_completed ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3" />
                              Betaald
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              In afwachting
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Tag className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                  <p className="text-zinc-500">Nog geen korting leads</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    Leads verschijnen hier wanneer bezoekers via een partner link korting claimen
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* BV Waitlist Tab */}
          {activeTab === "bv-waitlist" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <BVWaitlistSection password={password} />
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
